import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    Typography
} from '@mui/material';

// AD-2746 (Maria, 21 October 2026): a contact's Email Notifications are changed in a small box from the envelope on
// the contact's row, never in the Add new contact or Edit Contact Information boxes. Unticking asks first.
// AD-2645: the SIMS boxes the school chose on the MIS Integration tab are beside it, each with its own pop-up.
export const SIMS_BOX_LABELS = {
    ElectronicCommunication: 'InTouch Communication',
    PupilReport: 'Pupil Report',
    Correspondence: 'Correspondence'
};

// Maria, 21 October 2026: a parent reads 'you'; staff read the contact's forename
const subject = (contactForename, forParent) => (forParent ? 'you' : contactForename);

export const emailNotificationsWarning = (contactForename, studentName, forParent = false) =>
    `If you untick this box, ${subject(contactForename, forParent)} will not receive any information relating to ${studentName} from Admicity.`;

export const simsBoxWarning = (box, contactForename, studentName, forParent = false) => {
    const who = subject(contactForename, forParent);
    switch (box) {
        case 'PupilReport':
            return `If you untick this box, ${who} may not receive ${studentName}'s school reports.`;
        case 'Correspondence':
            return `If you untick this box, ${who} may not receive letters and other correspondence from the school about ${studentName}.`;
        default:
            return `If you untick this box, ${who} may not receive messages about ${studentName} from the school's communication system.`;
    }
};

const EMAIL = 'EmailNotifications';

const ContactNotificationsBox = ({open, contact, studentName, forParent, busy, onSave, onClose}) => {
    const [emailNotifications, setEmailNotifications] = useState(true);
    const [simsBoxes, setSimsBoxes] = useState({});
    const [askingToUntick, setAskingToUntick] = useState(null);

    useEffect(() => {
        if (open) {
            setEmailNotifications(contact?.emailNotificationsEnabled !== false);
            setSimsBoxes({...(contact?.simsBoxes ?? {})});
            setAskingToUntick(null);
        }
    }, [open, contact]);

    const tick = (box, checked) => {
        if (box === EMAIL) {
            setEmailNotifications(checked);
        } else {
            setSimsBoxes(boxes => ({...boxes, [box]: checked}));
        }
    };

    const handleChange = (box, checked) => {
        if (checked) {
            tick(box, true);
        } else {
            setAskingToUntick(box);
        }
    };

    const warning = askingToUntick === EMAIL
        ? emailNotificationsWarning(contact?.forename, studentName, forParent)
        : simsBoxWarning(askingToUntick, contact?.forename, studentName, forParent);

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth data-testid="contact-notifications-box">
                <DialogTitle sx={{pb: 1}}>
                    {[contact?.forename, contact?.surname].filter(Boolean).join(' ')}
                </DialogTitle>
                <DialogContent>
                    <Stack>
                        <FormControlLabel
                            control={<Checkbox
                                checked={emailNotifications}
                                onChange={(e) => handleChange(EMAIL, e.target.checked)}
                                inputProps={{'data-testid': 'contact-email-notifications'}}
                            />}
                            label="Email Notifications"
                        />
                        {Object.keys(SIMS_BOX_LABELS).filter(box => box in simsBoxes).map(box => (
                            <FormControlLabel
                                key={box}
                                control={<Checkbox
                                    checked={Boolean(simsBoxes[box])}
                                    onChange={(e) => handleChange(box, e.target.checked)}
                                    inputProps={{'data-testid': `contact-sims-${box}`}}
                                />}
                                label={SIMS_BOX_LABELS[box]}
                            />
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2}}>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={busy}
                        onClick={() => onSave({emailNotificationsEnabled: emailNotifications, simsBoxes})}
                        data-testid="contact-notifications-save"
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={open && Boolean(askingToUntick)} onClose={() => setAskingToUntick(null)} maxWidth="xs" data-testid="contact-notifications-warning">
                <DialogContent>
                    <Typography>{warning}</Typography>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2}}>
                    <Button onClick={() => setAskingToUntick(null)} data-testid="contact-notifications-keep">Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            tick(askingToUntick, false);
                            setAskingToUntick(null);
                        }}
                        data-testid="contact-notifications-untick"
                    >
                        Untick
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

ContactNotificationsBox.propTypes = {
    open: PropTypes.bool,
    contact: PropTypes.shape({
        forename: PropTypes.string,
        surname: PropTypes.string,
        emailNotificationsEnabled: PropTypes.bool,
        simsBoxes: PropTypes.objectOf(PropTypes.bool)
    }),
    studentName: PropTypes.string,
    forParent: PropTypes.bool,
    busy: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

export default ContactNotificationsBox;
